import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  Button,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { transformrBrand, brandFonts } from '@/theme/transformrTheme';
import { useDiscovery } from '../../hooks/useDiscovery';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { AnalysisProgressView } from '../AnalysisProgress/AnalysisProgress';
import { ConsentRequired } from '../ConsentRequired/ConsentRequired';
import { ReportDownload } from '../ReportDownload/ReportDownload';

/* Report generation runs in this modal (opened from the intake form) instead of
   a route: progress → "Your Report is Ready" content, all in place. The flow body
   is mounted only while the dialog is open so useDiscovery auto-starts on open
   and its polling cleans up on close. */

const useStyles = makeStyles({
  surface: {
    padding: 0,
    maxWidth: '38rem',
    width: '100%',
    overflow: 'hidden',
  },
  shell: {
    display: 'flex',
    flexDirection: 'column',
  },
  head: {
    position: 'relative',
    paddingTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    backgroundColor: transformrBrand.white,
    borderBottom: `1px solid ${transformrBrand.grey200}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  kicker: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
  },
  kickerRule: {
    display: 'inline-block',
    width: '2rem', // 32px accent rule — layout dim, no token
    height: '0.125rem', // 2px
    backgroundColor: transformrBrand.green,
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  title: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.02em',
    color: transformrBrand.navy,
    margin: 0,
  },
  note: {
    margin: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: transformrBrand.grey700,
  },
  close: {
    position: 'absolute',
    top: tokens.spacingVerticalL,
    right: tokens.spacingHorizontalL,
    color: transformrBrand.grey700,
  },
  body: {
    backgroundColor: transformrBrand.white,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    minHeight: '16rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

interface ReportRunDialogProps {
  open: boolean;
  onClose: () => void;
}

/** The live flow — mounted only while the dialog is open. */
function ReportRunFlow() {
  const discovery = useDiscovery();

  if (discovery.status === 'failed' && discovery.consentUrl) {
    return <ConsentRequired consentUrl={discovery.consentUrl} onReset={discovery.reset} />;
  }

  if (
    discovery.wizardStep === 'download' &&
    discovery.reportReady &&
    discovery.fileName
  ) {
    return <ReportDownload fileName={discovery.fileName} />;
  }

  return (
    <AnalysisProgressView
      currentStep={discovery.currentStep ?? null}
      stepsCompleted={discovery.stepsCompleted ?? []}
      progressPct={discovery.progressPct}
      message={discovery.message}
      error={discovery.error}
    />
  );
}

export function ReportRunDialog({ open, onClose }: ReportRunDialogProps) {
  const styles = useStyles();
  // Show the "takes up to a minute" note only while the report is generating,
  // not once it's ready to download.
  const wizardStep = useDiscoveryStore((s) => s.wizardStep);
  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface className={styles.surface}>
        <div className={styles.shell}>
          <div className={styles.head}>
            <span className={styles.kicker}>
              <span className={styles.kickerRule} aria-hidden="true" />
              AI Readiness · Analysis
            </span>
            <h2 className={styles.title}>Your AI Readiness Report</h2>
            {wizardStep === 'analysis' && (
              <p className={styles.note}>
                This can take up to a minute to generate — please keep this window open.
              </p>
            )}
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              aria-label="Close dialog"
              className={styles.close}
              onClick={onClose}
            />
          </div>
          <div className={styles.body}>
            {open && <ReportRunFlow />}
          </div>
        </div>
      </DialogSurface>
    </Dialog>
  );
}
